import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { uiNotification } from "../../../../services/UINotificationService";
import { Checkbox } from "../../../../shared/Checkbox/Checkbox";
import { ChartSmartSubscriptions } from "../ChartSmartSubscriptions/ChartSmartSubscription";
import { useSubscriptionDetailsQuery } from "../../../../api/chartSmart/useSubscriptionDetailsQuery";
import { useChangePlanMutation } from "../../../../api/chartSmart/useChangePlanMutation";
import Loader from "../../../../Components/Common/Loader";
import styles from "./TemplateSelector.module.scss";
import { CHARTSMART_PLAN } from "../../../../consts/ChartSmart";
import { extractApiError } from "../../../../utilities/api";
import { Modal } from "../../../../shared/Modal/Modal";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";

const formatTemplates = {
  soap: {
    id: "soap",
    label: "SOAP Note",
  },
  procedure: {
    id: "procedure",
    label: "Procedure Note",
  },
  surgical: {
    id: "surgical",
    label: "Surgical Note",
  },
  followup: {
    id: "followup",
    label: "Follow-up Visit",
  },
};

export function TemplateSelector({
  text,
  onChange,
  onClose,
  hideNoteSaveOption = false,
  subscriptionError,
  resetSubscriptionError,
  isSubmitting,
}) {
  const { tCommon } = useAppTranslation.Common();
  const { tSettings } = useAppTranslation.Settings();
  const { data: subscription, isLoading } = useSubscriptionDetailsQuery();

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [saveAsProcedure, setSaveAsProcedure] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const changePlan = useChangePlanMutation({
    onSuccess: () => {
      closeSubscriptions();
      submit();
      onClose();
      uiNotification.success(tSettings("subscriptions.success.select"));
    },
    onError: (res) => {
      const message = extractApiError(res);
      uiNotification.error(
        tSettings([
          `subscriptions.error.${message}`,
          "subscriptions.error.select",
        ]),
      );
    },
  });

  const isPayg = selectedPlan === CHARTSMART_PLAN.payg;

  const onChangePlan = () => {
    changePlan.mutateAsync({
      planName: isPayg ? subscription?.chartSmartPlan : selectedPlan,
      payg: isPayg,
    });
  };

  const templates = Object.entries(formatTemplates).map(([id, template]) => ({
    id,
    label: template.label,
  }));

  const submit = () => {
    if (!text) {
      uiNotification.error(tCommon("dictation.error.noTextToFormat"));
      return;
    }

    if (!selectedTemplateId && !customPrompt) {
      uiNotification.error(tCommon("dictation.error.selectTemplateOrPrompt"));
      return;
    }

    onChange({
      selectedTemplateId,
      customPrompt,
      saveAsProcedure,
    });
  };

  const handleSubmit = () => {
    if (isSubmitting || changePlan.isLoading) return;

    if (selectedPlan) {
      return onChangePlan();
    }

    submit();
  };

  const closeSubscriptions = () => {
    resetSubscriptionError();
  };

  return (
    <Modal isOpen withCloseBtn={false} className={styles.modal}>
      <div>
        {!subscriptionError && (
          <h1 className={styles.title}>
            {tCommon("dictation.title.selectTemplate")}
          </h1>
        )}

        <div
          className={clsx(styles.modalContentInner, {
            [styles.inner]: !subscriptionError,
          })}
        >
          {subscriptionError ? (
            <ChartSmartSubscriptions
              selectedPlan={selectedPlan}
              onSelectPlan={setSelectedPlan}
              onClose={onClose}
              subscriptionError={subscriptionError}
            />
          ) : (
            <div>
              <div className={styles.customPromptContainer}>
                <div className={styles.customPromptHeader}>
                  <h2>Create a Custom Prompt</h2>
                </div>
                <div className={styles.customPromptInputWrapper}>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder={tCommon("dictation.placeholder.customPrompt")}
                    className={styles.customPromptInput}
                  />
                </div>
              </div>

              <div className={styles.templateOptions}>
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplateId(template.id)}
                    className={clsx(
                      styles.templateButton,
                      selectedTemplateId === template.id &&
                        styles.templateButtonSelected,
                    )}
                  >
                    {template.label}
                  </button>
                ))}
              </div>

              <div className={styles.saveOption}>
                {!hideNoteSaveOption && (
                  <Checkbox
                    size="small"
                    inverted
                    isChecked={saveAsProcedure}
                    onChange={() => setSaveAsProcedure(!saveAsProcedure)}
                    label={tCommon("dictation.checkbox.saveAsProcedureNote")}
                  />
                )}
              </div>
            </div>
          )}

          <div className={styles.modalActions}>
            <button onClick={onClose} className={styles.cancelButton}>
              {tCommon("button.cancel")}
            </button>
            <button
              onClick={handleSubmit}
              className={styles.saveButton}
              disabled={
                isSubmitting ||
                changePlan.isLoading ||
                (subscriptionError
                  ? !selectedPlan
                  : !selectedTemplateId && !customPrompt)
              }
            >
              {isSubmitting || changePlan.isLoading ? (
                <CircularProgress size="small" color="white" />
              ) : null}
              {tCommon("button.save")}
            </button>
          </div>
        </div>
      </div>
      <Loader showLoader={isLoading} />
    </Modal>
  );
}

TemplateSelector.propTypes = {
  text: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  hideNoteSaveOption: PropTypes.bool,
};

TemplateSelector.defaultProps = {
  hideNoteSaveOption: false,
};
